import { useTranslation } from 'react-i18next';
import { Scale, Ruler, Tag } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';

interface ProductDetailsDisplayProps {
  gender?: string;
  weight?: number;
  weightUnit?: string;
  sizes: string[];
  tags: string[];
}

export function ProductDetailsDisplay({
  gender,
  weight,
  weightUnit,
  sizes,
  tags,
}: ProductDetailsDisplayProps) {
  const { t } = useTranslation();

  const hasContent = (gender && gender !== 'none') || weight || sizes.length > 0 || tags.length > 0;

  if (!hasContent) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('products.details')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {gender && gender !== 'none' && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t('products.gender')}</span>
            <span>{t(`products.genders.${gender}`, { defaultValue: gender })}</span>
          </div>
        )}

        {weight && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Scale className="size-4" />
              <span>{t('products.weight')}</span>
            </div>
            <span>{weight} {weightUnit || 'g'}</span>
          </div>
        )}

        {sizes.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Ruler className="size-4" />
              <span>{t('products.sizes')}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {sizes.map((size) => (
                <Badge key={size} variant="outline">{size}</Badge>
              ))}
            </div>
          </div>
        )}

        {tags.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Tag className="size-4" />
              <span>{t('products.tags')}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Badge key={tag} variant="secondary">{tag}</Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
